import { Inbox, Lock, Sparkles, Users } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { AuthorSide, type Finding, type FindingId, type Message } from "../data/review-data"

/**
 * Messages sheet.
 *
 * Backend contract (see api/routes_comments.py): a comment carries only
 * body, finding_id (optional), and a server-derived author_side. There is
 * no internal/client-visible flag — every comment on a review is equally
 * visible to every side that has access. Unread state is tracked per side,
 * not per user, so marking-read only fires when the panel opens.
 *
 * That is a deliberately narrow surface. If a reviewer needs a private
 * note, the product answer is a separate resource (a "reviewer note"),
 * not a boolean smuggled onto a shared message — because a mis-set
 * boolean is how confidential text leaks to a client.
 */

export interface MessagesSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  messages: Message[]
  findings: Finding[]
  activeFindingId: FindingId | null
  /** True when the review has an engagement — flavours the visibility line. */
  clientAttached: boolean
  onFocusFinding: (id: FindingId) => void
  onSend: (input: { body: string; findingId: FindingId | null }) => void
  onMarkRead: (findingId: FindingId | null) => void
}

type Scope = "all" | "general" | FindingId

export function MessagesSheet({
  open,
  onOpenChange,
  messages,
  findings,
  activeFindingId,
  clientAttached,
  onFocusFinding,
  onSend,
  onMarkRead,
}: MessagesSheetProps): JSX.Element {
  const [scope, setScope] = React.useState<Scope>(activeFindingId ?? "all")
  const [body, setBody] = React.useState("")
  const endRef = React.useRef<HTMLLIElement>(null)

  React.useEffect(() => {
    if (activeFindingId) setScope(activeFindingId)
  }, [activeFindingId])

  React.useEffect(() => {
    if (!open) return
    onMarkRead(scope !== "all" && scope !== "general" ? scope : null)
  }, [scope, open, onMarkRead])

  const filtered = React.useMemo(() => {
    if (scope === "all") return messages
    if (scope === "general") return messages.filter((m) => m.findingId === null)
    return messages.filter((m) => m.findingId === scope)
  }, [messages, scope])

  const lastMessageId = filtered[filtered.length - 1]?.id ?? null

  React.useLayoutEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" })
  }, [lastMessageId])

  const send = () => {
    const trimmed = body.trim()
    if (trimmed.length === 0) return
    const findingId = scope !== "all" && scope !== "general" ? scope : null
    onSend({ body: trimmed, findingId })
    setBody("")
  }

  const activeFindingChip =
    scope !== "all" && scope !== "general"
      ? findings.find((f) => f.id === scope) ?? null
      : null

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        size="md"
        title="Messages"
        description="Everyone on this review sees these messages. There is no private mode."
        maximizable
      >
        <div className="flex h-full min-h-0 flex-1 flex-col">
          <ScopePills scope={scope} onChange={setScope} findings={findings} messages={messages} />

          {activeFindingChip && (
            <AnchorCard finding={activeFindingChip} onJump={() => onFocusFinding(activeFindingChip.id)} />
          )}

          <div className="min-h-0 flex-1 overflow-auto px-5 py-5">
            {filtered.length === 0 ? (
              <EmptyThread scope={scope} />
            ) : (
              <ul className="space-y-4">
                {filtered.map((m, i) => (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    findings={findings}
                    showDayRule={
                      i === 0 || !isSameDay(filtered[i - 1]!.at, m.at)
                    }
                  />
                ))}
                <li ref={endRef} aria-hidden="true" />
              </ul>
            )}
          </div>

          <Composer
            body={body}
            onBodyChange={setBody}
            clientAttached={clientAttached}
            activeFindingChip={activeFindingChip}
            onSend={send}
          />
        </div>
      </SheetContent>
    </Sheet>
  )
}

/**
 * Scope pill row. Three canonical scopes lead ("Everything", "General",
 * and — when applicable — the currently-focused finding), then per-finding
 * pills scroll horizontally so a review with many findings does not push
 * the composer down.
 */
function ScopePills({
  scope,
  onChange,
  findings,
  messages,
}: {
  scope: Scope
  onChange: (s: Scope) => void
  findings: Finding[]
  messages: Message[]
}): JSX.Element {
  const countBy = React.useMemo(() => {
    const map = new Map<FindingId | "general" | "all", number>()
    map.set("all", messages.length)
    map.set("general", messages.filter((m) => m.findingId === null).length)
    for (const f of findings) {
      map.set(f.id, messages.filter((m) => m.findingId === f.id).length)
    }
    return map
  }, [messages, findings])

  return (
    <div className="shrink-0 border-b border-border-soft px-5 py-3">
      <div className="flex items-center gap-1.5">
        <span className="font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
          Filter
        </span>
        <span aria-hidden="true" className="h-3 w-px bg-border" />
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <Pill active={scope === "all"} onClick={() => onChange("all")} count={countBy.get("all")}>
            Everything
          </Pill>
          <Pill
            active={scope === "general"}
            onClick={() => onChange("general")}
            count={countBy.get("general")}
          >
            General
          </Pill>
          <span aria-hidden="true" className="mx-0.5 h-3 w-px shrink-0 bg-border" />
          {findings.map((f) => (
            <Pill
              key={f.id}
              active={scope === f.id}
              onClick={() => onChange(f.id)}
              title={f.claim}
              count={countBy.get(f.id)}
              index={f.number}
            >
              {f.ruleId}
            </Pill>
          ))}
        </div>
      </div>
    </div>
  )
}

interface PillProps {
  active: boolean
  onClick: () => void
  title?: string
  count?: number | undefined
  index?: number | undefined
  children: React.ReactNode
}

function Pill({
  active,
  onClick,
  title,
  count,
  index,
  children,
}: PillProps): JSX.Element {
  const hasCount = typeof count === "number" && count > 0
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      title={title}
      className={cn(
        "inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-medium transition-colors duration-120 ease-out",
        active
          ? "bg-text text-[color:var(--surface)]"
          : "bg-transparent text-text-2 hover:bg-surface-sunken hover:text-text",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
      )}
    >
      {typeof index === "number" && (
        <span
          className={cn(
            "font-id text-[10px] font-semibold tabular-nums",
            active ? "text-[color:var(--surface)]/60" : "text-text-3",
          )}
        >
          {index}
        </span>
      )}
      <span>{children}</span>
      {hasCount && (
        <span
          className={cn(
            "font-id text-[10.5px] tabular-nums",
            active ? "text-[color:var(--surface)]/60" : "text-text-3",
          )}
        >
          {count}
        </span>
      )}
    </button>
  )
}

/** Anchor card. Tapping it re-focuses the finding's overlay on the PDF. */
function AnchorCard({
  finding,
  onJump,
}: {
  finding: Finding
  onJump: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onJump}
      className="group mx-5 mt-3 flex shrink-0 items-start gap-3 rounded-[10px] border border-border bg-surface p-3 text-left transition-colors duration-120 ease-out hover:border-border-strong hover:bg-surface-sunken/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
    >
      <span className="grid size-6 shrink-0 place-items-center rounded-full bg-text font-id text-[10.5px] font-semibold tabular-nums text-[color:var(--surface)]">
        {finding.number}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="font-id text-[11px] font-semibold uppercase tracking-[0.06em] text-text">
            {finding.ruleId}
          </span>
          <span className="font-id text-[9.5px] uppercase tracking-[0.08em] text-text-3 group-hover:text-text-2">
            Tap to jump
          </span>
        </span>
        <span className="mt-1 line-clamp-2 block text-caption leading-[1.45] text-text-2">
          {finding.claim}
        </span>
      </span>
    </button>
  )
}

function EmptyThread({ scope }: { scope: Scope }): JSX.Element {
  const label =
    scope === "all"
      ? "on this review"
      : scope === "general"
        ? "in the general thread"
        : "on this finding"
  return (
    <div className="flex h-full items-center justify-center py-6">
      <div className="flex max-w-[30ch] flex-col items-center gap-2 text-center">
        <span className="grid size-8 place-items-center rounded-full bg-surface-sunken text-text-3">
          <Inbox className="size-4" aria-hidden="true" />
        </span>
        <p className="text-body-sm font-medium text-text">
          Nothing yet {label}
        </p>
        <p className="text-caption leading-[1.5] text-text-3">
          Start the conversation below. Everyone on this review will see it.
        </p>
      </div>
    </div>
  )
}

const SIDE_TAG: Record<AuthorSide, { label: string; className: string }> = {
  reviewer: { label: "Reviewer", className: "text-text-3" },
  client: { label: "Client", className: "text-print" },
  ai: { label: "AI", className: "text-info" },
}

const AVATAR_TONE: Record<AuthorSide, string> = {
  reviewer: "bg-info/90 text-white",
  client: "bg-print/90 text-white",
  ai: "bg-info-tint text-info",
}

function authorInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("")
}

/**
 * One message bubble.
 *
 * The chat pattern that reads correct across Slack/Linear/GitHub:
 *
 *   [avatar] [ metadata line ]
 *            [ bubble body    ]
 *
 * Avatar sits outside the bubble so it acts as a visual anchor per
 * author, not as part of the message body. Own messages mirror the
 * layout to the right. Bubbles are capped at ~62ch so lines stay
 * readable even when the sheet is maximized to modal width.
 *
 * AI-authored messages carry a Sparkles glyph on the avatar and an
 * info-tinted material on the bubble so provenance is obvious — the
 * reader always knows whether the sentence in front of them was
 * written by a person or a pipeline. Client bubbles get a print-tinted
 * material because the client side is a distinct principal.
 */
function MessageBubble({
  message,
  findings,
  showDayRule,
}: {
  message: Message
  findings: Finding[]
  showDayRule: boolean
}): JSX.Element {
  const attachedFinding = message.findingId
    ? findings.find((f) => f.id === message.findingId) ?? null
    : null
  const sideTag = SIDE_TAG[message.authorSide]
  const isAi = message.authorSide === AuthorSide.AI

  return (
    <>
      {showDayRule && (
        <li aria-hidden="true" className="flex items-center gap-2.5 py-1">
          <span className="h-px flex-1 bg-border-soft" />
          <span className="font-id text-[10px] uppercase tracking-[0.1em] text-text-3">
            {formatDay(message.at)}
          </span>
          <span className="h-px flex-1 bg-border-soft" />
        </li>
      )}
      <li
        className={cn(
          "flex items-start gap-2.5",
          message.mine ? "flex-row-reverse" : "flex-row",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "mt-4 grid size-7 shrink-0 place-items-center rounded-full font-id text-[10.5px] font-semibold leading-none",
            AVATAR_TONE[message.authorSide],
          )}
        >
          {isAi ? (
            <Sparkles className="size-3.5" aria-hidden="true" />
          ) : (
            authorInitials(message.authorName) || "?"
          )}
        </span>
        <div
          className={cn(
            "flex min-w-0 max-w-[min(62ch,86%)] flex-1 flex-col gap-1",
            message.mine && "items-end",
          )}
        >
          <div
            className={cn(
              "flex items-center gap-2 px-0.5 text-caption",
              message.mine && "flex-row-reverse",
            )}
          >
            <span className="truncate text-body-sm font-semibold text-text">
              {message.authorName}
            </span>
            <span
              className={cn(
                "shrink-0 font-id text-[10px] font-medium uppercase tracking-[0.08em]",
                sideTag.className,
              )}
            >
              {sideTag.label}
            </span>
            <span className="font-id text-[11px] tabular-nums text-text-3">
              {formatTime(message.at)}
            </span>
            {attachedFinding && (
              <span className="font-id text-[10.5px] font-medium text-text-3">
                · {attachedFinding.ruleId}
              </span>
            )}
          </div>
          <div
            className={cn(
              "whitespace-pre-wrap break-words rounded-2xl px-4 py-3 text-body-sm leading-[1.6]",
              message.mine
                ? "bg-brand-tint text-text"
                : isAi
                  ? "bg-info-tint/60 text-text"
                  : message.authorSide === AuthorSide.CLIENT
                    ? "bg-print-tint/60 text-text"
                    : "bg-surface-sunken text-text",
            )}
          >
            {message.body}
          </div>
        </div>
      </li>
    </>
  )
}

/**
 * Composer. Visibility is stated as a fact ("everyone on this review sees
 * this") rather than as a toggle, because the backend has no private
 * message concept. Making that a toggle would sell a control the server
 * cannot honour — and a mis-set toggle is precisely how confidential text
 * leaks to a client.
 */
function Composer({
  body,
  onBodyChange,
  clientAttached,
  activeFindingChip,
  onSend,
}: {
  body: string
  onBodyChange: (v: string) => void
  clientAttached: boolean
  activeFindingChip: Finding | null
  onSend: () => void
}): JSX.Element {
  const canSend = body.trim().length > 0
  return (
    <div className="shrink-0 border-t border-border-soft bg-surface px-5 pb-4 pt-3">
      <div>
        <Textarea
          minRows={2}
          maxRows={5}
          value={body}
          onChange={(e) => onBodyChange(e.currentTarget.value)}
          placeholder={
            activeFindingChip
              ? `Reply on ${activeFindingChip.ruleId}…`
              : "Write a message…"
          }
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault()
              onSend()
            }
          }}
          className="resize-none border-border-soft bg-surface-sunken/40 px-3.5 py-2.5 text-body-sm shadow-none placeholder:text-text-3 focus-visible:border-border-strong focus-visible:bg-surface focus-visible:outline-none focus-visible:ring-0"
        />
        <div className="mt-2 flex items-center gap-3">
          <span className="inline-flex min-w-0 items-center gap-1.5 text-caption text-text-3">
            {clientAttached ? (
              <>
                <Users className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">Visible to the client</span>
              </>
            ) : (
              <>
                <Lock className="size-3.5 shrink-0" aria-hidden="true" />
                <span className="truncate">Internal only</span>
              </>
            )}
          </span>
          <span aria-hidden="true" className="flex-1" />
          <kbd className="hidden shrink-0 font-id text-[10.5px] text-text-3 md:inline">
            ⌘⏎
          </kbd>
          <Button
            variant="primary"
            size="md"
            onClick={onSend}
            disabled={!canSend}
            aria-label="Send message"
          >
            Send
          </Button>
        </div>
      </div>
    </div>
  )
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatDay(iso: string): string {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date()
  yesterday.setDate(today.getDate() - 1)
  if (isSameDayDate(d, today)) return "Today"
  if (isSameDayDate(d, yesterday)) return "Yesterday"
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })
}

function isSameDay(a: string, b: string): boolean {
  return isSameDayDate(new Date(a), new Date(b))
}

function isSameDayDate(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}
